/**
 * Bank OAuth redirects land on `/open-finance/callback`. Keep third-party
 * analytics off this path (hash may still contain OAuth credentials until the
 * app consumes the Universal Link / custom-scheme handoff).
 */

export const OPEN_FINANCE_CALLBACK_PATH = '/open-finance/callback';
export const OPEN_FINANCE_CALLBACK_UNIVERSAL_LINK = `https://pierre.finance${OPEN_FINANCE_CALLBACK_PATH}`;

export function isOpenFinanceCallbackPath(
  pathname: string | null | undefined,
): boolean {
  if (!pathname) return false;
  return (
    pathname === OPEN_FINANCE_CALLBACK_PATH ||
    pathname.startsWith(`${OPEN_FINANCE_CALLBACK_PATH}/`)
  );
}

/** True when the fragment looks like an OAuth bank return (success or error). */
export function hasOpenFinanceOAuthHash(
  hash: string | null | undefined,
): boolean {
  if (!hash) return false;
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!fragment) return false;
  const params = new URLSearchParams(fragment);
  const state = params.get('state');
  if (!state) return false;
  return params.has('code') || params.has('error');
}

/** OAuth / correlation keys the mobile capture reads from query (preferred) or hash. */
const HANDOFF_QUERY_KEYS = [
  'code',
  'state',
  'id_token',
  'error',
  'error_description',
  'stateId',
] as const;

/**
 * Build the verified Universal Link the mobile app accepts for this callback.
 * Copies OAuth (+ stateId) from the hash into the query so iOS Linking does
 * not drop credentials when the fragment is stripped. Keeps the hash as-is.
 * Credentials are never posted from this page.
 */
export function buildPierreOpenFinanceCallbackUniversalLink(
  href: string,
): string {
  const source = new URL(href);
  const fragment = new URLSearchParams(
    source.hash.startsWith('#') ? source.hash.slice(1) : source.hash,
  );
  const search = new URLSearchParams(source.search);
  for (const key of HANDOFF_QUERY_KEYS) {
    if (search.has(key)) continue;
    const value = fragment.get(key);
    if (value !== null) search.set(key, value);
  }
  const destination = new URL(OPEN_FINANCE_CALLBACK_UNIVERSAL_LINK);
  destination.search = search.toString();
  destination.hash = source.hash;
  return destination.toString();
}
